/**
 * User Profiles service
 * Provides typed functions for interacting with the user_profiles table
 */

import { supabase } from '../supabase';
import type { UserProfile, UserRole } from '../types/database';

/**
 * Fetch the current authenticated user's profile
 * Returns null if user is not authenticated or profile doesn't exist
 */
export async function getUserProfile(
  userId: string | null | undefined
): Promise<UserProfile | null> {
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    // If profile doesn't exist, return null (shouldn't happen with auto-gen trigger, but handle gracefully)
    if (error.code === 'PGRST116') {
      return null;
    }
    throw new Error(`Failed to fetch user profile: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Fetch a user profile by ID (admin only in practice)
 */
export async function getUserProfileById(
  userId: string
): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    throw new Error(`Failed to fetch user profile: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Fetch all user profiles (admin directory use)
 */
export async function listUserProfiles(): Promise<UserProfile[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .order('full_name', { ascending: true, nullsFirst: false })
    .order('email', { ascending: true, nullsFirst: false });

  if (error) {
    throw new Error(`Failed to list user profiles: ${error.message}`);
  }

  return data as UserProfile[];
}

/**
 * Update the current user's profile
 */
export async function updateUserProfile(
  userId: string | null | undefined,
  updates: Partial<Pick<UserProfile, 'full_name' | 'email'>>
): Promise<UserProfile> {
  if (!userId) {
    throw new Error('User must be authenticated to update profile');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update user profile: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Update a user's role (admin only)
 */
export async function updateUserRole(
  userId: string,
  role: UserRole
): Promise<UserProfile> {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ role })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update user role: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Update multiple users' roles (admin only)
 */
export async function updateUserRolesBulk(
  userIds: string[],
  role: UserRole
): Promise<UserProfile[]> {
  if (userIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update({ role })
    .in('id', userIds)
    .select();

  if (error) {
    throw new Error(`Failed to update user roles: ${error.message}`);
  }

  return data as UserProfile[];
}

/**
 * Update a user's event visibility permission (admin only)
 * Allows non-admin users to see all events regardless of their role
 */
export async function updateUserEventVisibility(
  userId: string,
  canSeeAllEvents: boolean
): Promise<UserProfile> {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ can_see_all_events: canSeeAllEvents })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update user event visibility: ${error.message}`);
  }

  return data as UserProfile;
}

/**
 * Delete a user (admin only)
 * Uses Edge Function to ensure proper authorization and prevent self-deletion
 */
export async function deleteUser(userId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('delete-user', {
    body: { userId },
  });

  if (error) {
    throw new Error(error.message);
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Failed to delete user');
  }
}

/**
 * Delete multiple users (admin only)
 */
export async function deleteUsersBulk(userIds: string[]): Promise<void> {
  if (userIds.length === 0) {
    return;
  }

  const results = await Promise.allSettled(
    userIds.map((userId) => deleteUser(userId))
  );

  const failures = results
    .map((result, index) => ({ result, userId: userIds[index] }))
    .filter(({ result }) => result.status === 'rejected');

  if (failures.length > 0) {
    const failedIds = failures.map(({ userId }) => userId).join(', ');
    throw new Error(`Failed to delete users: ${failedIds}`);
  }
}
